import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePageStore } from "../stores/page-store";
import { ChannelChatSVG, ChannelLayoutSVG, LayoutType } from "remo-components";
import Icon from "@ant-design/icons";
import { VideoSVG } from "remo-components";
import { usePushHistory } from "../utils/routes";
import "./index.less";
import ChannelMsg from "../components/ChannelMsg";
import { Badge, Button, Popover, Tooltip } from "antd";
import { useChannelRoomStore, useChannelStore, useFriendStore } from "@obsbot/remo-stores";
import { eventBus } from "../../../remo-utils/src";
import { debounce } from "lodash-es";
import { ICustomPayloadDescription, SDKMsgType } from "@obsbot/remo-services";
import { useTranslate } from "@obsbot/remo-i18n";

const ChannelLayoutContainer: React.FC<{
    Component: React.ElementType;
}> = ({ Component }): ReactElement => {
    const channelStore = useChannelStore();
    const t = useTranslate();
    const { rtm, isJoinedRTC, roomUUID, blockedUsers } = useChannelRoomStore();
    const { conversation } = useFriendStore();
    // 配置不需要submenu
    const pageStore = usePageStore();
    const pushHistory = usePushHistory();
    const activeChannel = useMemo(() => channelStore.activeChannel, [channelStore.activeChannel]);
    const [isShowPopover, setIsShowPopover] = useState(false);
    const channelroomStore = useChannelRoomStore();
    const [isShowMessageDot, setIsShowMessageDot] = useState(false);
    // 当前频道的人数
    const [channelNum, setChannelNum] = useState(0);
    // 当前频道成员的头像
    const [channelAvatars, setChannelAvatars] = useState<string[]>([]);
    // 按钮的激活状态
    const [layoutActive, setLayoutActive] = useState(false);
    const [showActions, setShowActions] = useState<boolean>(true);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        pageStore.configure({
            subMenu: undefined,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        const replayHandler = (): void => {
            setIsShowPopover(false);
        };
        eventBus.on("replay-interaction", replayHandler);
        return () => eventBus.off("replay-interaction", replayHandler);
    });
    // 处理加入频道
    const handleJoinChannel = useCallback(() => {
        console.log("[WEB] 进入频道", activeChannel);
        if (!activeChannel) {
            return;
        }
        eventBus.emit("preview-camera", {
            isPreview: true,
            channelItem: activeChannel,
        });
    }, [activeChannel, pushHistory]);

    useEffect(() => {
        const rtm = channelroomStore.rtm;
        const unsubscribe = rtm?.events.on("message-received", (message: any): void => {
            const curChannelID = useChannelRoomStore.getState().roomUUID;
            // message.to === curChannelID && setIsShowMessageDot(true);
            const allowTypes = Object.values(SDKMsgType);
            const isAllowType = allowTypes.includes(message.type);
            const isToThisChannel = message.to === curChannelID;
            const isAutoMessage =
                message.type === SDKMsgType.MSG_CUSTOM &&
                message.payload.ICustomPayloadDescription ===
                    ICustomPayloadDescription.ChannelCommand;
            if (
                isAllowType &&
                isToThisChannel &&
                !isAutoMessage &&
                !blockedUsers.includes(message.from)
            ) {
                if (!isShowPopover) {
                    setIsShowMessageDot(true);
                }
                eventBus.emit("message-received", undefined);
            }
        });
        return () => {
            setIsShowMessageDot(false);
            unsubscribe?.();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channelroomStore.rtm, isShowPopover]);

    useEffect(() => {
        const queryMessageRead = async (): Promise<void> => {
            const convId = "GROUP" + roomUUID;
            const conversationInfo = await rtm?.getMessageList(convId);
            const curConversation = conversation.conversationList.find(
                item => item.conversationID === convId,
            );

            const unreadCount = curConversation?.unreadCount ?? 0;
            const list = conversationInfo?.messageList ?? [];

            if (unreadCount <= 0 || list.length === 0) {
                setIsShowMessageDot(false);
                return;
            }

            // 只检查最新的未读消息，避免越界与无效遍历
            const count = Math.min(unreadCount, list.length);
            const unreadMsgs = list.slice(-count);
            // 检查是否有未被屏蔽的未读消息
            const hasUnblockedUnread = unreadMsgs.some(
                msg => msg && !blockedUsers.includes(msg.from),
            );
            setIsShowMessageDot(hasUnblockedUnread);
        };
        queryMessageRead();
    }, [rtm, roomUUID, blockedUsers, conversation.conversationList]);

    // 进入频道后8s无操作将隐藏actions
    const resetTimer = useCallback(() => {
        if (!isJoinedRTC) {
            return;
        }
        setShowActions(true);
        if (isShowPopover || layoutActive) {
            return;
        }
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => {
            setShowActions(false);
        }, 8000);
    }, [isJoinedRTC, isShowPopover, layoutActive]);
    // 监听有任何点击重新展示actions
    useEffect(() => {
        if (!isJoinedRTC) {
            return;
        }
        resetTimer();
        const handleClick = (): void => {
            resetTimer();
        };
        document.addEventListener("click", handleClick);
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            document.removeEventListener("click", handleClick);
        };
    }, [resetTimer, isJoinedRTC, isShowPopover, layoutActive]);

    const queryNum = useRef(
        debounce(async (): Promise<void> => {
            const activeChannel = useChannelStore.getState().activeChannel;
            if (!activeChannel) {
                return;
            }
            if (!localStorage.getItem("token")) {
                return;
            }
            if (!rtm) {
                return;
            }
            try {
                const list = await channelStore.queryChannelNum(activeChannel.channelUuid);
                setChannelNum(list.data.length);
                if (list.data.length) {
                    const profiles = await rtm.getUserProfile([...list.data].slice(0, 5));
                    setChannelAvatars([...profiles.map(profile => profile.avatar)]);
                }
            } catch (error) {
                console.error("[WEB] 查看频道成员数失败", error);
                setChannelNum(0);
            }
        }, 200),
    );

    useEffect(() => {
        const fn = queryNum.current;
        eventBus.on("query-channel-room-list", fn);

        return () => eventBus.off("query-channel-room-list", fn);
    }, []);

    useEffect(() => {
        queryNum.current();
    }, [activeChannel]);
    return (
        <div className="channel-page-main">
            {isJoinedRTC && (
                <div className={"channel-outter-top-bar show-action-" + showActions}>
                    <div className="channel-name">
                        <Icon component={VideoSVG} />
                        <span>{activeChannel?.channelName}</span>
                    </div>
                    <div className="channel-actions">
                        <Popover
                            content={<LayoutType />}
                            open={layoutActive}
                            rootClassName="channel-action-layout"
                            trigger={["click"]}
                            onOpenChange={(open: boolean) => {
                                setLayoutActive(open);
                                setIsShowPopover(false);
                            }}
                        >
                            <Tooltip arrow={false} placement="bottom" title={t("chat.layout")}>
                                <Button className="cp channel-layout" size="small" type="text">
                                    <ChannelLayoutSVG isActive={layoutActive} />
                                </Button>
                            </Tooltip>
                        </Popover>
                        <Popover
                            // 为了销毁 ChannelMsg 中监听的接收到消息设置已读的事件
                            destroyOnHidden
                            content={<ChannelMsg />}
                            open={isShowPopover}
                            rootClassName="channel-msg-popover"
                            trigger={["click"]}
                            onOpenChange={(open: boolean) => {
                                if (!isJoinedRTC) {
                                    return;
                                }
                                console.log("[WEB] 点击消息弹窗", open);
                                setIsShowPopover(open);
                                setLayoutActive(false);
                                if (open) {
                                    setIsShowMessageDot(false);
                                    roomUUID && rtm?.setMessageRead("GROUP" + roomUUID);
                                }
                            }}
                        >
                            <Tooltip
                                arrow={false}
                                placement="bottom"
                                title={t("chat.interactive-msg")}
                            >
                                <Button size="small" type="text">
                                    <Badge
                                        dot={isShowMessageDot && !isShowPopover}
                                        offset={[-9, 9]}
                                    >
                                        <div className="cp channel-message">
                                            <ChannelChatSVG isActive={isShowPopover} />
                                        </div>
                                    </Badge>
                                </Button>
                            </Tooltip>
                        </Popover>
                    </div>
                </div>
            )}
            <Component
                avatars={channelAvatars}
                channelCount={channelNum}
                channelName={useChannelStore.getState().activeChannel?.channelName}
                hasChannel={!!useChannelStore.getState().channels.size}
                onClickCreateChannel={() => eventBus.emit("show-create-channel", undefined)}
                onClickJoinChannel={handleJoinChannel}
            />
        </div>
    );
};

// 使用 withRemoServices 包裹组件
export default ChannelLayoutContainer;
